import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Terminal, CheckCircle2, Network, Zap, Brain, Layers, Gauge } from "lucide-react";

const FEATURES = [
  {
    icon: Terminal,
    title: "Real IOS Command Feel",
    body: "Muscle memory that transfers. enable, conf t, show run—authentic prompts and syntax errors built for the exam.",
  },
  {
    icon: Brain,
    title: "Instant Feedback",
    body: "Paste nothing. Click Grade My Work. Get a score, what's wrong, and the exact commands to fix it.",
  },
  {
    icon: Network,
    title: "Mapped to CCNA 200-301",
    body: "Six domains, 30+ labs. Subnetting, OSPF, VLANs, NAT, ACLs, Port Security, automation concepts.",
  },
  {
    icon: Gauge,
    title: "Exam Readiness Score",
    body: "A weighted 0-100 number that answers the real question: am I ready for the test?",
  },
];

const DOMAIN_PREVIEW = [
  ["01", "Network Fundamentals", "20%"],
  ["02", "Network Access", "20%"],
  ["03", "IP Connectivity", "25%"],
  ["04", "IP Services", "10%"],
  ["05", "Security", "15%"],
  ["06", "Automation", "10%"],
];

export default function Landing() {
  const { user } = useAuth();
  const signedIn = user && user !== false;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100">
      {/* Top nav */}
      <header className="border-b border-zinc-900">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-[#00E5FF] text-black">
              <Terminal size={16} strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-lg">
              Hypex<span className="text-[#00E5FF]">Labs</span>
            </span>
          </div>
          <nav className="flex items-center gap-6 text-sm text-zinc-400">
            <a href="#features" className="hover:text-white hidden sm:block">Features</a>
            <a href="#domains" className="hover:text-white hidden sm:block">Curriculum</a>
            {signedIn ? (
              <Link
                to="/dashboard"
                className="px-4 py-1.5 bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC]"
                data-testid="nav-dashboard-cta"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="hover:text-white" data-testid="nav-login">Log in</Link>
                <Link
                  to="/signup"
                  className="px-4 py-1.5 bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC]"
                  data-testid="nav-signup"
                >
                  Start Free
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-70" />
        <div className="relative max-w-[1800px] mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-12 px-6 pt-20 pb-24">
          <div className="flex flex-col gap-8 animate-fade-up">
            <div className="inline-flex items-center gap-2 w-fit px-3 py-1 border border-[#00FF41]/30 text-[#00FF41] text-[10px] uppercase tracking-[0.3em] font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF41] animate-pulse" />
              CCNA 200-301 · Built for engineers
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-none tracking-tighter">
              Ship the cert.
              <br />
              Skip the <span className="text-[#00E5FF] text-glow-cyan">hardware</span>.
            </h1>
            <p className="text-lg text-zinc-400 max-w-xl leading-relaxed">
              HypexLabs is the practice platform for the CCNA. Type real
              IOS commands in your browser, click <span className="text-[#00FF41] font-term">Grade My Work</span>,
              and get a score with the exact fixes. No CML, no packet tracer frustration, no hardware.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <Link
                to={signedIn ? "/dashboard" : "/signup"}
                className="px-6 py-3 bg-[#00E5FF] text-black font-semibold tracking-tight flex items-center gap-2 hover:bg-[#00B8CC] transition-colors glow-cyan"
                data-testid="hero-cta-primary"
              >
                Start your first lab <ArrowRight size={16} />
              </Link>
              <a
                href="#features"
                className="px-6 py-3 border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-white transition-colors"
                data-testid="hero-cta-secondary"
              >
                See how it works
              </a>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 pt-4 text-xs text-zinc-500 font-mono">
              <span className="flex items-center gap-1">
                <CheckCircle2 size={12} className="text-[#00FF41]" /> 72 labs included
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 size={12} className="text-[#00FF41]" /> Instant feedback with exact fix commands
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 size={12} className="text-[#00FF41]" /> Free forever tier
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 size={12} className="text-[#00FF41]" /> Practice anywhere. Any device.
              </span>
            </div>
          </div>

          {/* Terminal preview */}
          <div className="hidden lg:block">
            <div className="bg-black border border-zinc-800 shadow-[0_0_80px_rgba(0,229,255,0.08)] overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-900 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
                <span className="w-2 h-2 rounded-full bg-[#00FF41] animate-pulse" />
                console · R1 · ospf-single-area
              </div>
              <div className="p-5 font-term text-sm space-y-1">
                <div><span className="text-[#00FF41]">R1&gt;</span> <span className="text-zinc-100">enable</span></div>
                <div><span className="text-[#00FF41]">R1#</span> <span className="text-zinc-100">configure terminal</span></div>
                <div className="text-zinc-500 text-xs">Enter configuration commands, one per line. End with CNTL/Z.</div>
                <div><span className="text-[#00FF41]">R1(config)#</span> <span className="text-zinc-100">router ospf 1</span></div>
                <div><span className="text-[#00FF41]">R1(config-router)#</span> <span className="text-zinc-100">router-id 1.1.1.1</span></div>
                <div><span className="text-[#00FF41]">R1(config-router)#</span> <span className="text-zinc-100">network 10.1.1.0 0.0.0.255 area 0</span></div>
                <div><span className="text-[#00FF41]">R1(config-router)#</span> <span className="text-zinc-100">end</span></div>
                <div className="pt-3 text-[#00E5FF] text-xs">
                  ▸ Instant feedback: 92/100 &nbsp;·&nbsp; 4/5 checks passed &nbsp;·&nbsp;
                  <span className="text-[#F59E0B]">missing: router-id on process 1</span>
                </div>
                <div className="flex items-center pt-2">
                  <span className="text-[#00FF41] mr-2">R1#</span>
                  <span className="w-2 h-4 bg-[#00FF41] animate-caret-blink inline-block" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-zinc-900 py-24">
        <div className="max-w-[1800px] mx-auto px-6">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#00E5FF] font-mono mb-3">
            // what you get
          </div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-12 max-w-2xl">
            Everything you need to pass, nothing you don't.
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="bg-[#141416] border border-zinc-800 p-8 hover:-translate-y-1 hover:border-zinc-600 transition-all"
                  data-testid={`feature-card-${f.title.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="w-10 h-10 border border-[#00E5FF]/40 flex items-center justify-center mb-5">
                    <Icon size={18} className="text-[#00E5FF]" />
                  </div>
                  <h3 className="font-display font-bold text-xl mb-2">{f.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Domains */}
      <section id="domains" className="border-t border-zinc-900 py-24 bg-[#0a0a0a]">
        <div className="max-w-[1800px] mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#00E5FF] font-mono mb-3">
                // curriculum
              </div>
              <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
                Six domains. 54 hands-on labs.
              </h2>
            </div>
            <Link
              to={signedIn ? "/labs" : "/signup"}
              className="text-sm text-[#00E5FF] hover:text-white flex items-center gap-2 font-mono"
              data-testid="domain-browse"
            >
              browse all labs <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {DOMAIN_PREVIEW.map(([n, name, w]) => (
              <div
                key={n}
                className="bg-[#141416] border border-zinc-800 p-6 flex items-start gap-4 hover:border-zinc-600 transition-colors"
                data-testid={`domain-card-${n}`}
              >
                <div className="font-term text-2xl text-[#00E5FF]">{n}</div>
                <div className="flex-1">
                  <div className="font-display font-semibold">{name}</div>
                  <div className="text-xs text-zinc-500 mt-1 font-mono">weight · {w}</div>
                </div>
                <Layers size={16} className="text-zinc-600" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-zinc-900 py-24">
        <div className="max-w-[1800px] mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-zinc-800 text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-mono mb-6">
            <Zap size={10} /> 60-second lab spin-up
          </div>
          <h2 className="font-display font-black text-5xl lg:text-6xl tracking-tighter mb-6 max-w-3xl mx-auto">
            The cert doesn't wait. Neither should you.
          </h2>
          <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
            Create your account, pick a weak area, and get your first lab in under a minute.
          </p>
          <Link
            to={signedIn ? "/dashboard" : "/signup"}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#00E5FF] text-black font-bold hover:bg-[#00B8CC] transition-colors glow-cyan"
            data-testid="footer-cta"
          >
            Begin lab #1 <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-zinc-900 py-8">
        <div className="max-w-[1800px] mx-auto px-6 flex items-center justify-between text-xs text-zinc-600 font-mono">
          <div>© HypexLabs · not affiliated with Cisco Systems</div>
          <div className="text-zinc-700">CCNA 200-301</div>
        </div>
      </footer>
    </div>
  );
}
